import React, { useState } from "react";
import {
  ArrowUpRight, Globe, ChevronDown, RefreshCw, Download, Share2,
} from "lucide-react";

// Placeholder imagery from existing assets — swap thumbnails/previews with
// real product screenshots when available.
import stoneThumb from "@/assets/luxury-sculpture.jpg";
import stonePreview from "@/assets/providedassets/biggerstone1.jpg";
import stoneMaterialImage from "@/assets/providedassets/smallstone1.jpg";
import tileThumb from "@/assets/providedassets/tile1.jpg";
import tilePreview from "@/assets/providedassets/tile2.jpg";
import tileMaterialImage from "@/assets/providedassets/smalltile1.jpg";
import panelThumb from "@/assets/wabi-sabi-lighting.jpg";
import panelPreview from "@/assets/providedassets/panel2.jpg";
import panelMaterialImage from "@/assets/providedassets/smallerpanel.jpg";
import furnitureThumb from "@/assets/providedassets/furniituresmaller.jpeg";
import furniturePreview from "@/assets/providedassets/furniturelarger.jpeg";
import furnitureMaterialImage from "@/assets/providedassets/furniituresmaller.jpeg";
import curtainThumb from "@/assets/providedassets/curtails1.jpg";
import curtainPreview from "@/assets/providedassets/biggercurtains.jpg";
import curtainMaterialImage from "@/assets/providedassets/smallercurtains.jpg";
import rugThumb from "@/assets/providedassets/rugs1.jpg";
import rugPreview from "@/assets/providedassets/rugs2.jpg";
import rugMaterialImage from "@/assets/providedassets/smallerugs.jpg";

const products = [
  {
    id: "stone",
    label: "Stone Visualizer",
    dropdownLabel: "All walls",
    materialName: "Statuario Classic",
    materialSize: "Size: 9x5 ft",
    materialThumbnail: stoneThumb,
    materialImage: stoneMaterialImage,
    previewImage: stonePreview,
    hasBookmatchToggle: true,
  },
  {
    id: "tile",
    label: "Tile Visualizer",
    dropdownLabel: "All floors",
    materialName: "Carrara Tile",
    materialSize: "Size: 12x12 in",
    materialThumbnail: tileThumb,
    materialImage: tileMaterialImage,
    previewImage: tilePreview,
    hasBookmatchToggle: false,
  },
  {
    id: "panel",
    label: "Panel Visualizer",
    dropdownLabel: "All walls",
    materialName: "Walnut Fluted Panel",
    materialSize: "Size: 8x4 ft",
    materialThumbnail: panelThumb,
    materialImage: panelMaterialImage,
    previewImage: panelPreview,
    hasBookmatchToggle: false,
  },
  {
    id: "furniture",
    label: "Furniture Visualizer",
    dropdownLabel: "All rooms",
    materialName: "Curved Boucle Sofa",
    materialSize: "Size: 8x3 ft",
    materialThumbnail: furnitureThumb,
    materialImage: furnitureMaterialImage,
    previewImage: furniturePreview,
    hasBookmatchToggle: false,
  },
  {
    id: "curtains",
    label: "Curtain Visualizer",
    dropdownLabel: "All windows",
    materialName: "Ivory Linen Drape",
    materialSize: "Size: 7x4 ft",
    materialThumbnail: curtainThumb,
    materialImage: curtainMaterialImage,
    previewImage: curtainPreview,
    hasBookmatchToggle: false,
  },
  {
    id: "rugs",
    label: "Rug Visualizer",
    dropdownLabel: "All floors",
    materialName: "Persian Heritage Rug",
    materialSize: "Size: 8x10 ft",
    materialThumbnail: rugThumb,
    materialImage: rugMaterialImage,
    previewImage: rugPreview,
    hasBookmatchToggle: false,
  },
];

function BookmatchToggle() {
  const [on, setOn] = useState(true);
  return (
    <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
      <span className="text-xs font-semibold text-neutral-700">Bookmatch</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => setOn((v) => !v)}
        className={`relative h-5 w-9 rounded-full transition-colors ${on ? "bg-neutral-900" : "bg-neutral-300"}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
            on ? "translate-x-[18px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

export default function DemoShowcase() {
  const [activeProductId, setActiveProductId] = useState(products[0].id);
  const active = products.find((p) => p.id === activeProductId);

  return (
    <section id="demo" className="bg-neutral-100 py-16 sm:py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-center lg:gap-16">
          {/* Left column — product list */}
          <div className="lg:w-72 lg:shrink-0">
            <h2 className="text-2xl font-bold text-neutral-900 sm:text-3xl md:text-4xl">
              Try our demo for free
            </h2>
            <ul
              className="-mx-4 mt-5 flex gap-2 overflow-x-auto px-4 pb-1 [-ms-overflow-style:none] [scrollbar-width:none] lg:mx-0 lg:mt-8 lg:flex-col lg:gap-0 lg:space-y-1.5 lg:overflow-visible lg:px-0 lg:pb-0 [&::-webkit-scrollbar]:hidden"
            >
              {products.map((product) => {
                const isActive = product.id === activeProductId;
                return (
                  <li key={product.id} className="shrink-0 lg:w-full">
                    <button
                      type="button"
                      onClick={() => setActiveProductId(product.id)}
                      className={`flex w-full items-center justify-between whitespace-nowrap rounded-full px-4 py-2.5 text-left text-sm font-medium transition-all lg:px-5 lg:py-3 lg:text-base ${
                        isActive
                          ? "bg-white text-neutral-900 shadow-md shadow-neutral-900/10"
                          : "text-neutral-500 hover:bg-white/60 hover:text-neutral-800"
                      }`}
                    >
                      {product.label}
                      {isActive && <ArrowUpRight className="ml-1.5 h-4 w-4 shrink-0 lg:ml-0" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right column — mockup card */}
          <div className="min-w-0 flex-1">
            <div className="overflow-hidden rounded-2xl bg-white shadow-xl shadow-neutral-900/10">
              {/* Window dots */}
              <div className="flex items-center gap-1.5 border-b border-neutral-100 px-5 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" />
              </div>

              {/* App header bar */}
              <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-4 py-3 sm:gap-3 sm:px-5">
                <p className="min-w-0 flex-1 truncate text-sm font-bold text-neutral-900">
                  Your Company Logo
                </p>
                <div className="hidden items-center gap-4 md:flex">
                  <span className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600">
                    <Globe className="h-3.5 w-3.5" />
                    English
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-neutral-500">
                    <RefreshCw className="h-3.5 w-3.5" />
                    Change space
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-neutral-500">
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </span>
                  <span className="flex items-center gap-1.5 text-xs font-medium text-neutral-500">
                    <Share2 className="h-3.5 w-3.5" />
                    Share
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                  <span className="whitespace-nowrap rounded-full bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-white sm:px-4">
                    Get estimate
                  </span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-xs font-bold text-neutral-600">
                    A
                  </span>
                </div>
              </div>

              {/* Mockup body */}
              <div className="flex flex-col-reverse md:flex-row">
                {/* Mini panel — re-renders (with fade) per active product */}
                <div
                  key={active.id}
                  className="w-full shrink-0 space-y-3 border-t border-neutral-100 bg-neutral-50/60 p-4 duration-200 animate-in fade-in sm:space-y-4 sm:p-5 md:w-60 md:border-t-0 md:border-r"
                >
                  <p className="text-xs leading-relaxed text-neutral-500">
                    Select a surface, pick a material, and generate a realistic
                    preview of your space.
                  </p>

                  {/* Dropdown */}
                  <div className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white px-3 py-2.5">
                    <span className="text-xs font-semibold text-neutral-700">
                      {active.dropdownLabel}
                    </span>
                    <ChevronDown className="h-4 w-4 text-neutral-400" />
                  </div>

                  {/* Material card */}
                  <div className="rounded-xl border border-neutral-200 bg-white p-3">
                    <img
                      key={active.id}
                      src={active.materialImage}
                      alt={active.materialName}
                      className="h-20 w-full rounded-lg object-cover sm:h-24"
                    />
                    <p className="mt-2.5 text-sm font-bold text-neutral-900">
                      {active.materialName}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-400">{active.materialSize}</p>
                  </div>

                  {active.hasBookmatchToggle && <BookmatchToggle />}

                  <button
                    type="button"
                    className="w-full rounded-full bg-neutral-900 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-neutral-700"
                  >
                    Generate image
                  </button>
                </div>

                {/* Preview image — cross-fades per active product */}
                <div className="relative min-h-[260px] flex-1 sm:min-h-[320px] md:min-h-[420px]">
                  <img
                    key={active.id}
                    src={active.previewImage}
                    alt={`${active.label} preview`}
                    className={`absolute inset-0 h-full w-full object-cover duration-200 animate-in fade-in ${
                      active.id === "rugs" ? "object-bottom" : "object-center"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
